import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { useApi } from "@/api/hooks/useApi";
import type { AnswerRequest, CreateSurveyRequestDto, UpdateSurveyRequestDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

const useAddSurvey = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: CreateSurveyRequestDto) => api.postSurveysV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({
        type: "success",
        title: t("page.surveys-create-or-edit.new.notification.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SURVEYS(projectId) });
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.surveys-create-or-edit.new.notification.error") });
    },
  });
};

const useUpdateSurvey = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ surveyId, data }: { surveyId: string; data: UpdateSurveyRequestDto }) =>
      api.putSurveysV1(surveyId, data).then((x) => x.data),
    onSuccess: (_, payload) => {
      showFlashToast({
        type: "success",
        title: t("page.surveys-create-or-edit.edit.notification.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTOMATED_SURVEY_DETAILS(payload.surveyId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SURVEYS(projectId) });
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.surveys-create-or-edit.edit.notification.error") });
    },
  });
};

const useDeleteSurvey = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const navigate = useNavigate();
  const slug = useSlug();

  return useMutation({
    mutationFn: ({ surveyId }: { surveyId: string }) => api.deleteSurveysByIdV1(surveyId),
    onSuccess: () => {
      showFlashToast({ type: "success", title: t("page.surveys.actions.delete.notifications.success") });

      void navigate(routes.surveys.list({ slug }));
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.surveys.actions.delete.notifications.failed") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SURVEYS(projectId) });
    },
  });
};

const useAddFollowUpDate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ surveyId }: { surveyId: string }) => api.postSurveysFollowUpV1(surveyId),
    onMutate: (payload) => {
      // Optimistically update the survey details in the cache
      queryClient.setQueryData<ApiResponseType<"getSurveysDetailsV1">>(
        QUERY_KEYS.SURVEY_DETAILS(projectId, payload.surveyId),
        (currData) => {
          if (!currData) {
            return currData;
          }

          return {
            ...currData,
            data: {
              ...currData.data,
              // This is the clilent-side date, which is not 100% matching with
              // server-side date, but it's sufficient for optimistic update
              followUpDate: new Date().toISOString(),
            },
          };
        },
      );
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SURVEY_DETAILS(projectId, payload.surveyId) });
    },
  });
};

const useAddSurveyAnswers = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ surveyId, data }: { surveyId: string; data: AnswerRequest }) =>
      api.postSurveysAnswerV1(surveyId, data),
    onMutate: (payload) => {
      // Optimistically update the survey details in the cache
      queryClient.setQueryData<ApiResponseType<"getSurveysDetailsV1">>(
        QUERY_KEYS.SURVEY_DETAILS(projectId, payload.surveyId),
        (currData) => {
          if (!currData) {
            return currData;
          }

          return {
            ...currData,
            data: {
              ...currData.data,
              canRespond: "alreadyAnswered",
            },
          };
        },
      );
    },

    onError: () => {
      showFlashToast({ type: "error", title: t("page.surveys.actions.respond.notifications.failed") });
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SURVEY_DETAILS(projectId, payload.surveyId) });
    },
  });
};

const useRateSurveyFollowUp = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({
      surveyId,
      followUpId,
      rating,
    }: {
      surveyId: string;
      followUpId: string;
      rating: "positive" | "negative";
    }) => api.postSurveysSuggestedFollowUpsRateV1(surveyId, followUpId, rating),
    onMutate: (payload) => {
      // Optimistically update the survey suggested follow-ups in the cache
      queryClient.setQueryData<ApiResponseType<"getSurveysSuggestedFollowUpsV1">>(
        QUERY_KEYS.SURVEY_SUGGESTED_FOLLOW_UPS(projectId, payload.surveyId),
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const clonedData = structuredClone(oldData.data);
          const followUp = clonedData.find((f) => f.id === payload.followUpId);
          if (followUp) {
            followUp.rating = payload.rating;
            followUp.ratedAt = new Date().toISOString();
          }

          return { ...oldData, data: clonedData };
        },
      );
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SURVEY_SUGGESTED_FOLLOW_UPS(projectId, payload.surveyId),
      });
    },
  });
};

export const surveyMutations = {
  useAddSurvey,
  useUpdateSurvey,
  useDeleteSurvey,
  useAddFollowUpDate,
  useAddSurveyAnswers,
  useRateSurveyFollowUp,
};
